import { NavigateFunction } from 'react-router-dom';

export interface WorkspaceContext {
    workspaceId?: string;
    projectId?: string;
}

export interface RouteContext extends WorkspaceContext {
    id?: string;
    [key: string]: string | undefined;
}

export interface NavigationOptions {
    replace?: boolean;
}

interface PathBuilder {
    buildPath(path: string): string;
    buildPathWithQuery(path: string, queryParams: URLSearchParams): string;
}

class WorkspacePathBuilder implements PathBuilder {
    private readonly workspaceId?: string;
    private readonly projectId?: string;

    constructor(context: WorkspaceContext) {
        this.workspaceId = context.workspaceId;
        this.projectId = context.projectId;
    }

    buildPath(path: string): string {
        // Special handling for box paths
        if (path.startsWith('/box/')) {
            return path;
        }

        if (this.workspaceId && this.projectId) {
            return `/w/${this.workspaceId}/p/${this.projectId}${path}`;
        }

        if (this.workspaceId) {
            return `/w/${this.workspaceId}${path}`;
        }

        return path;
    }

    buildPathWithQuery(path: string, queryParams: URLSearchParams): string {
        const basePath = this.buildPath(path);
        const queryString = queryParams.toString();
        return queryString ? `${basePath}?${queryString}` : basePath;
    }
}

export interface ContextAwareNavigator {
    navigateTo(path: string, options?: NavigationOptions): void;
    navigateToWithQuery(path: string, queryParams: URLSearchParams, options?: NavigationOptions): void;
    buildPath(path: string): string;
}

export class RouterNavigator implements ContextAwareNavigator {
    private readonly navigate: NavigateFunction;
    private readonly pathBuilder: PathBuilder;

    constructor(navigate: NavigateFunction, context: WorkspaceContext) {
        this.navigate = navigate;
        this.pathBuilder = new WorkspacePathBuilder(context);
    }

    navigateTo(path: string, options?: NavigationOptions): void {
        this.navigate(this.pathBuilder.buildPath(path), options);
    }

    navigateToWithQuery(path: string, queryParams: URLSearchParams, options?: NavigationOptions): void {
        this.navigate(this.pathBuilder.buildPathWithQuery(path, queryParams), options);
    }

    buildPath(path: string): string {
        return this.pathBuilder.buildPath(path);
    }
}

export function createNavigator(navigate: NavigateFunction, context: WorkspaceContext): ContextAwareNavigator {
    return new RouterNavigator(navigate, context);
}

export function extractRouteParams<T extends RouteContext = RouteContext>(
    routeParams: Record<string, string | undefined>
): T {
    return routeParams as T;
}
